import { useMemo, useState } from 'react';
import { fetchTerritoryCatalog } from '../api/client';
import { useAsync } from '../api/useCatalog';
import type { TerritoryCategory } from '../types/catalog';
import { TERRITORY_CATEGORY } from '../i18n/labels';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { TerritoryTile } from '../components/TerritoryTile';

const CATEGORIES: TerritoryCategory[] = ['light', 'medium', 'heavy'];

export function TerritoriesPage(): React.JSX.Element {
  const { data, loading, error, reload } = useAsync(fetchTerritoryCatalog);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<TerritoryCategory | null>(null);

  const rules = useMemo(() => data?.rules ?? [], [data]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rules.filter((t) => {
      if (category && t.category !== category) return false;
      if (query && !`${t.name} ${t.description}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [rules, search, category]);

  return (
    <>
      <div className="page-head">
        <h1>Territórios</h1>
        <p>
          Cada partida acontece sobre territórios, e cada um impõe uma regra própria que muda como
          aquele espaço funciona. A intensidade do efeito é indicada pela categoria.
          {data && <> Catálogo v{data.version}.</>}
        </p>
      </div>

      <AsyncBoundary loading={loading} error={error} onRetry={reload}>
        <div className="filterbar">
          <div className="filter-row">
            <input
              type="search"
              className="search-input"
              placeholder="Buscar território…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar territórios"
            />
            {(search !== '' || category !== null) && (
              <button
                type="button"
                className="btn-clear"
                onClick={() => {
                  setSearch('');
                  setCategory(null);
                }}
              >
                Limpar filtros
              </button>
            )}
          </div>
          <div className="filter-row">
            <span className="filter-label">Categoria</span>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`chip ${category === cat ? 'active' : ''}`}
                aria-pressed={category === cat}
                onClick={() => setCategory((cur) => (cur === cat ? null : cat))}
                title={TERRITORY_CATEGORY[cat].help}
              >
                {TERRITORY_CATEGORY[cat].label}
              </button>
            ))}
          </div>
        </div>

        <p className="result-count">
          {filtered.length === rules.length
            ? `${rules.length} territórios`
            : `${filtered.length} de ${rules.length} territórios`}
        </p>

        {filtered.length === 0 ? (
          <div className="state">Nenhum território corresponde aos filtros.</div>
        ) : (
          <div className="territory-grid">
            {filtered.map((territory) => (
              <TerritoryTile key={territory.ruleId} territory={territory} />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </>
  );
}
